
import { mapState } from "vuex";

import { PAGE_TYPE, QUERY_STORE_LOCATOR } from "./-store-locator.query";
import { seoMixin, pageMixin } from "~/mixins";

const UID = "store-locator";

export default {
  mixins: [seoMixin, pageMixin],

  async asyncData({ app }) {
    const { [PAGE_TYPE]: data } = await app.$prismicGql.request({
      query: QUERY_STORE_LOCATOR,
      variables: { uid: UID },
    });

    return {
      title: data.title,
      metaTitle: data.metaTitle,
      campaignSaleUID: data.campaign_sale_link?._meta?.uid,
      uid: UID,
      pageType: PAGE_TYPE,
      metaDescription: data.metaDescription,
      metaRobots: data.metaRobots,
      metaFields: { _linkType: data._linkType, _meta: data._meta },
    };
  },

  data: () => ({
    stores: [],
    center: null,
    selected: "",
    error: "",
    loading: false,
    postcodeEntered: false,
    map: null,
    places: null,
  }),

  computed: {
    ...mapState(["siteConfig"]),
    ...mapState("app", ["isGoogleMapLoaded"]),
    additionalScripts() {
      const { googleMapsApiKey } = this.$config;

      return [
        {
          hid: "google-maps-init",
          pbody: true,
          type: "text/javascript",
          innerHTML: `function initMap() {}`,
        },
        {
          skip: this.isGoogleMapLoaded,
          once: true,
          hid: "google-maps",
          src: `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=geometry&callback=initMap`,
          async: true,
          callback: async () => {
            await this.$store.dispatch("app/setGoogleMapLoaded");
          },
        },
      ];
    },
  },

  watch: {
    stores(values) {
      if (values.length !== 0) {
        this.$gtm.gtagEvent("store_find_a_store");
      }
    },
  },

  mounted() {
    const unwatch = this.$watch("isGoogleMapLoaded", async function (value) {
      if (!value) return;
      this.initGoogleMap();

      try {
        this.loading = true;
        const { latitude, longitude } = await this.handleGeoLocation();
        this.postcodeEntered = true;
        const postcode = await this.getPostCode({ latitude, longitude });
        await this.updateGoogleMap({ postcode });
      } catch (err) {
        this.$bugsnag.notify(err);
      } finally {
        this.loading = false;
      }

      unwatch();
    });
  },

  methods: {
    async updateGoogleMap({ postcode, suburb }) {
      await this.fetchStores({ postcode, suburb });
      this.addStoresToMap();
      this.highlightStore();
    },
    handleGeoLocation() {
      const { timeout, maxAge: maximumAge } = this.siteConfig.storeLocator;
      return new Promise(resolve => {
        navigator?.geolocation?.getCurrentPosition(
          ({ coords: { latitude, longitude } }) => {
            this.center = { lat: latitude, lng: longitude };
            resolve({ latitude, longitude });
          },
          err => {
            console.warn(new Error(`ERROR(${err.code}): ${err.message}`));
          },
          { enableHighAccuracy: true, timeout, maximumAge }
        );
      });
    },
    initGoogleMap() {
      const { zoom, mapCenter: center } = this.siteConfig.storeLocator;
      const snoozeMap = this.$refs["snooze-map"];
      if (snoozeMap) {
        this.map = new window.google.maps.Map(snoozeMap, { zoom, center });
      }
    },
    async handlePlaceChange({ suburb = "", postcode = "" } = {}) {
      this.postcodeEntered = true;
      this.loading = true;
      await this.updateGoogleMap({ suburb, postcode });
      this.loading = false;
      //   this.center = { lat: location.lat(), lng: location.lng() };
    },
    async getPostCode({ longitude, latitude }) {
      // const { data } = await this.$axios.get(
      //   this.$config.googleMapGeocode.replace(
      //     "__LATITUDE_LONGITUDE__",
      //     `${latitude},${longitude}`
      //   )
      // );
      // const addresses = data?.results?.[0]?.address_components ?? [];
      // const postcode = addresses.find(item =>
      //   item.types.find(el => el == "postal_code")
      // );
      const { data } = await this.$axios.get(
        this.$config.geocodeMapClient
          .replace("__LATITUDE__", `${latitude}`)
          .replace("__LONGITUDE__", `${longitude}`)
      );
      return parseInt(data.postcode);
      // return parseInt(postcode?.long_name);
    },
    highlightStore(index = 0) {
      this.map?.setOptions({
        zoom: this.siteConfig.storeLocator.mapZoom,
        center: { lat: this.stores[index]?.lat, lng: this.stores[index]?.lng },
      });
    },
    addStoresToMap() {
      const googleMaps = window?.google?.maps;

      if (!this.stores.length || !googleMaps) {
        return false;
      }

      let activeInfoWindow = null;
      for (let store of this.stores) {
        const marker = new googleMaps.Marker({
          position: { lat: store.lat, lng: store.lng },
          map: this.map,
          icon: "//cdn.shopify.com/s/files/1/2657/7564/files/map-pin.png",
          title: store.name,
        });

        const infoWindow = new googleMaps.InfoWindow({
          content: this.getInfoContent(store),
        });

        // Attach it to the marker we've just added
        googleMaps.event.addListener(marker, "click", () => {
          this.selected = store.id;
          if (activeInfoWindow) {
            activeInfoWindow.close();
          }
          infoWindow.open(this.map, marker);
          activeInfoWindow = infoWindow;

          // scroll div to
          const $store = document.getElementById(`store-${store.id}`);
          const container = document.getElementById("scroll-container");
          if ($store && container) {
            container.scrollTop = $store.offsetTop - container.offsetTop;
          }
        });
      }
    },
    getInfoContent(store) {
      return `<div class="infowindow w-57">
          <a href="${store.url}" target="_blank">
            <h4 class="text-xl mb-4">${store.name}</h4>
            <p class="text-sm">${store.street}</p>
            <p class="text-sm">${store.suburb}, ${store.state}, ${store.postcode}</p>
            <p class="text-sm mb-4">${store.country}</p>
          </a>
          <div class="">
            <h5 class="text-lg mb-2">
              <a href="tel:${store.telephone}"> ${store.telephone} </a>
            </h5>
            <h6 class="text-sm mb-2">
              <a href="mailto:${store.email}"> ${store.email} </a>
            </h6>
          <div>
          <a
            href="https://www.google.com/maps/dir/?api=1&amp;destination=${store.formatted_address}"
            target="_blank"
            rel="noreferrer"
            data-external-link
          >
            Directions
          </a>
        </div>`;
    },
    async fetchStores({ postcode, suburb = "%27%27" } = {}) {
      this.stores = [];
      if (!postcode) {
        this.error = "Please enter postcode";
        return false;
      }

      if (postcode && postcode.length > 4) {
        this.error = "Please enter valid postcode";
        return false;
      }

      this.error = "";
      this.selected = "";
      try {
        const { data } = await this.$axios.get(
          `${this.$config.snoozeEcom}/postcodestores?postcode=${postcode}&suburb=${suburb}`
        );

        this.stores = this.formatStores(data?.data);
      } catch (err) {
        this.$bugsnag.notify(err);
      }
    },
    formatStores(stores) {
      return stores.map(store => {
        const storeName = store.name
          .replace(/\s/g, "-")
          .replace(/[&\\#,+()$~%.'":*?<>{}]/g, "")
          .toLowerCase();

        const street = store.formatted_address
          ? store.formatted_address
              .replace(new RegExp(`${store.country}\\s?$`), "")
              .replace(new RegExp(`${store.postcode}\\s?,\\s?$`), "")
              .replace(new RegExp(`${store.state}\\s?,\\s?$`), "")
              .replace(new RegExp(`${store.suburb}\\s?,\\s?$`), "")
              .trim()
          : store.name;

        return { ...store, url: `/store/${storeName}`, street };
      });
    },
  },
};
